import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Clock, ExternalLink, User, Sparkles, Building2, TrendingUp, Brain,
  ShieldAlert, Cpu, Loader2, Zap, BarChart3, Info, MapPin, Briefcase,
  Linkedin, CheckCircle, AlertTriangle, XCircle, ChevronDown, ChevronUp,
  Target, Swords, Activity, ArrowUpRight, Globe, Users, DollarSign,
  Newspaper, Copy, Check, Shield, RefreshCw, AlignLeft
} from 'lucide-react';
import { formatDate } from '../../../../../shared/utils/date';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../../../../shared/components/atoms/Button';

interface BriefingDossierProps {
  selectedEventId: string | null;
  briefingEventId: string | null;
  loadingBriefing: boolean;
  briefingDetail: any;
  activeTab: 'overview' | 'prospect' | 'company' | 'tactical' | 'battlecards';
  setActiveTab: (tab: 'overview' | 'prospect' | 'company' | 'tactical' | 'battlecards') => void;
  isCrmConnected: boolean;
  connectedCrmName: string | null;
  onViewIntelligence: (eventId: string) => void;
  onRegenerateIntelligence: (eventId: string) => void;
}

// ── AI Generating State Component ──────────────────────────────────────────────
const AI_STEPS = [
  { icon: <Cpu size={13} />, label: 'Parsing calendar & attendees' },
  { icon: <BarChart3 size={13} />, label: 'Querying CRM pipeline data' },
  { icon: <Zap size={13} />, label: 'Profiling prospect & company' },
  { icon: <Brain size={13} />, label: 'Generating tactical briefing' },
];

function AiGeneratingState({ message, error = false, pending = false }: { message: string; error?: boolean; pending?: boolean }) {
  const [activeStep, setActiveStep] = useState(0);
  const [dots, setDots] = useState('');
  useEffect(() => {
    if (error || pending) return;
    const stepInterval = setInterval(() => setActiveStep((s) => (s + 1) % AI_STEPS.length), 1400);
    const dotInterval = setInterval(() => setDots((d) => (d.length >= 3 ? '' : d + '.')), 500);
    return () => { clearInterval(stepInterval); clearInterval(dotInterval); };
  }, [error, pending]);

  return (
    <motion.div key="ai-generating" initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }}
      style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '48px 32px', gap: '28px', textAlign: 'center' }}>
      <div style={{ position: 'relative', width: '72px', height: '72px' }}>
        {!error && (
          <motion.div animate={{ scale: [1, 1.35, 1], opacity: [0.3, 0, 0.3] }} transition={{ duration: 2.2, repeat: Infinity, ease: 'easeInOut' }}
            style={{ position: 'absolute', inset: 0, borderRadius: '50%', border: '1.5px solid #3E8FA6' }} />
        )}
        <motion.div animate={error || pending ? {} : { boxShadow: ['0 0 0px #3E8FA6', '0 0 22px #3E8FA6', '0 0 0px #3E8FA6'] }}
          transition={{ duration: 2, repeat: Infinity }}
          style={{ position: 'absolute', inset: '12px', borderRadius: '50%', backgroundColor: error ? 'rgba(239,68,68,0.08)' : 'rgba(62,143,166,0.10)', border: `1.5px solid ${error ? 'rgba(239,68,68,0.3)' : 'rgba(62,143,166,0.35)'}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {error ? <ShieldAlert size={22} style={{ color: '#ef4444', opacity: 0.8 }} />
            : pending ? <Sparkles size={22} style={{ color: '#a78bfa', opacity: 0.9 }} />
              : <motion.div animate={{ rotate: 360 }} transition={{ duration: 3, repeat: Infinity, ease: 'linear' }}><Cpu size={22} style={{ color: '#3E8FA6' }} /></motion.div>}
        </motion.div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
        <span style={{ fontSize: '14px', fontWeight: 800, color: 'var(--admin-card-text)', letterSpacing: '-0.2px' }}>
          {error ? 'Could not load briefing' : pending ? 'Brief Queued — AI Processing' : `${message}${dots}`}
        </span>
        <span style={{ fontSize: '11px', color: 'var(--admin-card-subtext)', lineHeight: 1.5, maxWidth: '320px' }}>
          {error ? 'There was an issue fetching the briefing for this event. Please select another meeting or try refreshing.'
            : pending ? 'The AI engine has received the request and is building your intelligence dossier. It will appear here automatically once ready.'
              : 'FinnAI is cross-referencing calendar data, CRM records, and competitive intelligence to generate your custom pre-call brief.'}
        </span>
      </div>
      {!error && !pending && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', width: '100%', maxWidth: '280px' }}>
          {AI_STEPS.map((step, i) => (
            <motion.div key={i} animate={{ opacity: activeStep === i ? 1 : 0.35 }} transition={{ duration: 0.3 }}
              style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '6px 10px', borderRadius: '4px', backgroundColor: activeStep === i ? 'rgba(62, 143, 166, 0.08)' : 'transparent', border: activeStep === i ? '1px solid rgba(62, 143, 166, 0.2)' : '1px solid transparent', transition: 'background 0.3s, border 0.3s' }}>
              <span style={{ color: activeStep === i ? '#3E8FA6' : 'var(--admin-card-subtext)', flexShrink: 0 }}>{step.icon}</span>
              <span style={{ fontSize: '11px', fontWeight: activeStep === i ? 700 : 500, color: activeStep === i ? 'var(--admin-card-text)' : 'var(--admin-card-subtext)' }}>{step.label}</span>
              {activeStep === i && (<motion.div animate={{ opacity: [1, 0.4, 1] }} transition={{ duration: 0.8, repeat: Infinity }} style={{ marginLeft: 'auto', width: '6px', height: '6px', borderRadius: '50%', backgroundColor: '#3E8FA6', flexShrink: 0 }} />)}
            </motion.div>
          ))}
        </div>
      )}
      {pending && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '5px 12px', borderRadius: '20px', backgroundColor: 'rgba(167, 139, 250, 0.08)', border: '1px solid rgba(167, 139, 250, 0.2)' }}>
          <motion.div animate={{ opacity: [1, 0.3, 1] }} transition={{ duration: 1.2, repeat: Infinity }}><Loader2 size={11} style={{ color: '#a78bfa' }} /></motion.div>
          <span style={{ fontSize: '10px', fontWeight: 700, color: '#a78bfa', letterSpacing: '0.05em', textTransform: 'uppercase' }}>AI Engine Processing</span>
        </div>
      )}
    </motion.div>
  );
}

// ── Sub-components ─────────────────────────────────────────────────────────────

function InfoBlock({ label, value, color }: { label: string; value: React.ReactNode; color?: string }) {
  return (
    <div>
      <div style={{ fontSize: '10px', color: 'var(--admin-card-subtext)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '2px' }}>{label}</div>
      <div style={{ fontSize: '13px', color: color || 'var(--admin-card-text)', fontWeight: 700 }}>{value || <span style={{ color: 'var(--admin-card-subtext)', fontWeight: 400, fontStyle: 'italic' }}>—</span>}</div>
    </div>
  );
}

function SectionCard({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) {
  return (
    <div style={{ padding: '12px 14px', border: '1px solid var(--admin-card-border)', backgroundColor: 'rgba(255,255,255,0.015)', ...style }}>
      {children}
    </div>
  );
}

function SectionTitle({ icon, label, badge }: { icon: React.ReactNode; label: string; badge?: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '7px', marginBottom: '12px' }}>
      <span style={{ color: '#3E8FA6', display: 'flex' }}>{icon}</span>
      <span style={{ fontSize: '11px', fontWeight: 800, color: 'var(--admin-card-text)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</span>
      {badge}
    </div>
  );
}

function RsvpBadge({ status }: { status: string }) {
  const map: Record<string, { label: string; color: string; icon: React.ReactNode }> = {
    accepted:     { label: 'Accepted',    color: '#10b981', icon: <CheckCircle size={10} /> },
    declined:     { label: 'Declined',    color: '#ef4444', icon: <XCircle size={10} /> },
    tentative:    { label: 'Tentative',   color: '#f59e0b', icon: <AlertTriangle size={10} /> },
    needsAction:  { label: 'No Response', color: '#94a3b8', icon: <Clock size={10} /> },
  };
  const s = map[status] || map.needsAction;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '10px', fontWeight: 700, color: s.color, padding: '2px 7px', border: `1px solid ${s.color}33`, backgroundColor: `${s.color}11` }}>
      {s.icon}{s.label}
    </span>
  );
}

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  const handle = () => { navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 1800); };
  return (
    <button onClick={handle} title="Copy" style={{ background: 'none', border: 'none', cursor: 'pointer', color: copied ? '#10b981' : 'var(--admin-card-subtext)', padding: '2px', display: 'flex', alignItems: 'center' }}>
      {copied ? <Check size={11} /> : <Copy size={11} />}
    </button>
  );
}

function EngagementGauge({ score, label }: { score: number; label: string }) {
  const colorMap: Record<string, string> = {
    highly_engaged: '#10b981',
    engaged:        '#22c55e',
    lukewarm:       '#f59e0b',
    cold:           '#f97316',
    ghosting:       '#ef4444',
  };
  const color = colorMap[label] || '#38bdf8';
  const radius = 40;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (score / 100) * circumference;
  const labelText = label?.replace(/_/g, ' ') || 'Unknown';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px' }}>
      <div style={{ position: 'relative', width: '100px', height: '100px' }}>
        <svg width="100" height="100" viewBox="0 0 100 100" style={{ transform: 'rotate(-90deg)' }}>
          <circle cx="50" cy="50" r={radius} fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="10" />
          <motion.circle cx="50" cy="50" r={radius} fill="none" stroke={color} strokeWidth="10"
            strokeLinecap="butt" strokeDasharray={circumference} initial={{ strokeDashoffset: circumference }}
            animate={{ strokeDashoffset: offset }} transition={{ duration: 1.2, ease: 'easeOut' }} />
        </svg>
        <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
          <span style={{ fontSize: '22px', fontWeight: 900, color, fontFamily: "'Space Grotesk', sans-serif", lineHeight: 1 }}>{score}</span>
          <span style={{ fontSize: '9px', color: 'var(--admin-card-subtext)', fontWeight: 600 }}>/ 100</span>
        </div>
      </div>
      <span style={{ fontSize: '10px', fontWeight: 800, color, textTransform: 'uppercase', letterSpacing: '0.08em' }}>{labelText}</span>
    </div>
  );
}

function TechChip({ label }: { label: string }) {
  return (
    <span style={{ fontSize: '10px', fontWeight: 600, color: '#38bdf8', backgroundColor: 'rgba(56, 189, 248, 0.07)', border: '1px solid rgba(56, 189, 248, 0.2)', padding: '2px 7px', whiteSpace: 'nowrap' }}>
      {label}
    </span>
  );
}

// ── Main Dossier Component ──────────────────────────────────────────────────────

export function BriefingDossier({
  selectedEventId, briefingEventId, loadingBriefing, briefingDetail,
  activeTab, setActiveTab, isCrmConnected, connectedCrmName, onViewIntelligence, onRegenerateIntelligence
}: BriefingDossierProps) {
  const navigate = useNavigate();
  const [subscoredExpanded, setSubscoresExpanded] = useState(false);
  const [newsExpanded, setNewsExpanded] = useState(false);
  const [techExpanded, setTechExpanded] = useState(false);
  const [activeCompetitor, setActiveCompetitor] = useState(0);

  const briefingRequested = loadingBriefing || (briefingDetail && briefingEventId === selectedEventId);
  const b = briefingDetail?.briefing;
  const ai = b;

  // Tabs config — overview always shown, AI tabs shown with/without data (locked state)
  const TABS = [
    { id: 'overview' as const,    label: 'Overview',     icon: <Activity size={13} /> },
    { id: 'prospect' as const,    label: 'Prospect',     icon: <User size={13} /> },
    { id: 'company' as const,     label: 'Company',      icon: <Building2 size={13} /> },
    { id: 'tactical' as const,    label: 'Call Strategy',     icon: <Target size={13} /> },
    { id: 'battlecards' as const, label: 'Battlecards',  icon: <Swords size={13} /> },
  ];

  return (
    <div style={{ border: '1px solid var(--admin-card-border)', borderRadius: '0', backgroundColor: 'var(--admin-card-bg)', boxShadow: '0 1px 2px rgba(0,0,0,0.05)', display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>
      <AnimatePresence mode="wait">
        {!selectedEventId ? (
          /* ── No Event Selected ── */
          <motion.div key="empty" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: 'var(--admin-card-subtext)', padding: '48px', textAlign: 'center', gap: '12px' }}>
            <Brain size={48} className="text-sky-400 opacity-20 mb-2" />
            <span style={{ fontSize: '14px', fontWeight: 800, color: 'var(--admin-card-text)' }}>No Call Selected</span>
            <span style={{ fontSize: '12px', maxWidth: '320px', lineHeight: 1.5 }}>Select an upcoming meeting from the sidebar to generate an AI-powered intelligence dossier.</span>
          </motion.div>

        ) : !briefingRequested ? (
          /* ── CTA: Generate Brief ── */
          <motion.div key={`cta-${selectedEventId}`} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
            style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '48px 32px', gap: '24px', textAlign: 'center' }}>
            <div style={{ position: 'relative' }}>
              <motion.div animate={{ scale: [1, 1.12, 1], opacity: [0.2, 0.4, 0.2] }} transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
                style={{ position: 'absolute', inset: '-8px', borderRadius: '50%', border: '1.5px solid #3E8FA6' }} />
              <div style={{ width: '64px', height: '64px', borderRadius: '50%', backgroundColor: 'rgba(62, 143, 166, 0.08)', border: '1.5px solid rgba(62, 143, 166, 0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Sparkles size={26} style={{ color: '#3E8FA6' }} />
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <span style={{ fontSize: '15px', fontWeight: 800, color: 'var(--admin-card-text)', letterSpacing: '-0.2px' }}>Generate Intelligence Dossier</span>
              <span style={{ fontSize: '11px', color: 'var(--admin-card-subtext)', lineHeight: 1.6, maxWidth: '320px' }}>FinnAI will cross-reference calendar data, CRM records, LinkedIn profiling, and competitive signals to build a custom pre-call brief for this meeting.</span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', width: '100%', maxWidth: '320px' }}>
              {[{ icon: <User size={11} />, label: 'Prospect & Engagement' }, { icon: <Building2 size={11} />, label: 'Company Intel' }, { icon: <Target size={11} />, label: 'Call Strategy' }, { icon: <Swords size={11} />, label: 'Competitor Battlecards' }].map((item, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 10px', borderRadius: '4px', backgroundColor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.04)' }}>
                  <span style={{ color: '#3E8FA6', flexShrink: 0 }}>{item.icon}</span>
                  <span style={{ fontSize: '10px', fontWeight: 600, color: 'var(--admin-card-subtext)' }}>{item.label}</span>
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
              <motion.button whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }} onClick={() => onViewIntelligence(selectedEventId!)}
                style={{ display: 'flex', alignItems: 'center', gap: '8px', background: 'linear-gradient(135deg, #2d7a93, #3E8FA6)', color: '#ffffff', border: 'none', borderRadius: '0', padding: '10px 24px', fontSize: '13px', fontWeight: 700, cursor: 'pointer', boxShadow: '0 4px 16px rgba(62, 143, 166, 0.25)', fontFamily: 'inherit' }}>
                <Cpu size={14} />Generate Intelligence Brief
              </motion.button>
              <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                <Zap size={10} style={{ color: '#f59e0b' }} />
                <span style={{ fontSize: '10px', color: 'var(--admin-card-subtext)', fontWeight: 600 }}>Costs 5 credits per brief</span>
              </div>
            </div>
          </motion.div>

        ) : loadingBriefing ? (
          <AiGeneratingState key="loading" message="Analyzing prospect profile" />
        ) : !briefingDetail ? (
          <AiGeneratingState key="error" message="Fetching event data" error />
        ) : !b ? (
          <AiGeneratingState key="no-briefing" message="AI brief is being prepared" pending />
        ) : (
          /* ── Full Dossier ── */
          <motion.div key={`dossier-${briefingEventId}`} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
            style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>

            {/* ── Header ── */}
            <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--admin-card-border)', backgroundColor: 'rgba(255,255,255,0.01)', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px' }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                  <h2 style={{ fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, fontSize: '20px', lineHeight: '28px', letterSpacing: '-0.4px', color: '#F6F9FB', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '380px' }}>
                    {briefingDetail.event.title}
                  </h2>
                  {new Date(briefingDetail.event.end_time) < new Date() && (
                    <span style={{ fontSize: '9px', fontWeight: 800, color: '#ef4444', backgroundColor: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)', padding: '1px 6px' }}>Past Meeting</span>
                  )}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '5px', flexWrap: 'wrap' }}>
                  <span style={{ fontSize: '11px', color: 'var(--admin-card-subtext)', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <Clock size={11} />{formatDate(briefingDetail.event.start_time, true, true)}
                  </span>
                  {briefingDetail.event.meeting_url && (
                    <a href={briefingDetail.event.meeting_url} target="_blank" rel="noopener noreferrer"
                      style={{ fontSize: '11px', color: '#3E8FA6', textDecoration: 'none', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <ExternalLink size={11} />Join Meeting
                    </a>
                  )}
                  {ai.prospect_profile?.linkedin_url && (
                    <a href={ai.prospect_profile.linkedin_url.startsWith('http') ? ai.prospect_profile.linkedin_url : `https://${ai.prospect_profile.linkedin_url}`}
                      target="_blank" rel="noopener noreferrer"
                      style={{ fontSize: '11px', color: '#0077b5', textDecoration: 'none', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <Linkedin size={11} />LinkedIn
                    </a>
                  )}
                  {briefingDetail.event.location && (
                    <span style={{ fontSize: '11px', color: 'var(--admin-card-subtext)', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <MapPin size={11} />{briefingDetail.event.location}
                    </span>
                  )}
                </div>
                {briefingDetail.event.description && (
                  <div style={{ marginTop: '8px', fontSize: '11px', color: 'var(--admin-card-subtext)', lineHeight: 1.4, display: 'flex', gap: '6px', alignItems: 'flex-start', maxWidth: '500px' }}>
                    <AlignLeft size={12} style={{ marginTop: '2px', flexShrink: 0 }} />
                    <span style={{ wordBreak: 'break-word', overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                      {briefingDetail.event.description}
                    </span>
                  </div>
                )}
              </div>
              <div style={{ flexShrink: 0, textAlign: 'right', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '6px' }}>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                  <div style={{ fontSize: '9px', color: 'var(--admin-card-subtext)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '4px' }}>Generated</div>
                  <span style={{ fontSize: '10px', fontWeight: 800, color: '#3E8FA6', backgroundColor: 'rgba(62,143,166,0.1)', border: '1px solid rgba(62,143,166,0.2)', padding: '3px 8px' }}>
                    {ai.generated_at ? new Date(ai.generated_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : 'AI Brief'}
                  </span>
                </div>
                <button
                  onClick={() => onRegenerateIntelligence(selectedEventId!)}
                  title="Regenerate this briefing (Costs 5 Credits)"
                  style={{ display: 'flex', alignItems: 'center', gap: '4px', background: 'transparent', border: 'none', color: 'var(--admin-card-subtext)', fontSize: '10px', fontWeight: 600, cursor: 'pointer', padding: '2px 4px', borderRadius: '4px', marginTop: '2px' }}
                >
                  <RefreshCw size={10} /> Regenerate
                </button>
              </div>
            </div>

            {/* ── Tabs ── */}
            <div style={{ display: 'flex', borderBottom: '1px solid var(--admin-card-border)', backgroundColor: 'rgba(255,255,255,0.005)', overflowX: 'auto' }}>
              {TABS.map((tab) => (
                <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                  style={{ flex: '1 1 auto', padding: '9px 10px', background: 'none', border: 'none', borderBottom: activeTab === tab.id ? '2px solid #3E8FA6' : '2px solid transparent', color: activeTab === tab.id ? '#3E8FA6' : 'var(--admin-card-subtext)', fontWeight: activeTab === tab.id ? 800 : 600, fontSize: '11px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px', whiteSpace: 'nowrap', transition: 'all 0.2s', fontFamily: 'inherit' }}>
                  {tab.icon}{tab.label}
                </button>
              ))}
            </div>

            {/* ── Content Body ── */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: '12px' }}>

              {/* ═══════ TAB: OVERVIEW ═══════ */}
              {activeTab === 'overview' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>

                  {/* Meeting Header Row */}
                  <SectionCard>
                    <SectionTitle icon={<Activity size={14} />} label="Meeting Details" />
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px 16px' }}>
                      <InfoBlock label="Start Time" value={formatDate(briefingDetail.event.start_time, true, true)} />
                      <InfoBlock label="End Time" value={formatDate(briefingDetail.event.end_time, true, true)} />
                      <InfoBlock label="Prospect" value={briefingDetail.event.prospect_email} />
                    </div>
                  </SectionCard>

                  {/* Participants */}
                  {briefingDetail.event.participants?.length > 0 && (
                    <SectionCard>
                      <SectionTitle icon={<Users size={14} />} label="Attendees & RSVP"
                        badge={<span style={{ fontSize: '10px', fontWeight: 700, color: 'var(--admin-card-subtext)', backgroundColor: 'rgba(255,255,255,0.05)', padding: '1px 6px', border: '1px solid var(--admin-card-border)' }}>{briefingDetail.event.participants.length}</span>} />
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        {briefingDetail.event.participants.map((p: any, i: number) => (
                          <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '7px 10px', backgroundColor: 'rgba(255,255,255,0.01)', border: '1px solid rgba(255,255,255,0.03)' }}>
                            <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                              <span style={{ fontSize: '12px', fontWeight: 700, color: 'var(--admin-card-text)' }}>{p.name}</span>
                              <span style={{ fontSize: '10px', color: 'var(--admin-card-subtext)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.email} · {p.role}</span>
                            </div>
                            <RsvpBadge status={p.status} />
                          </div>
                        ))}
                      </div>
                    </SectionCard>
                  )}

                  {/* CRM Snapshot (always from raw data) */}
                  {(b?.crm_history?.salesforce || b?.crm_history?.hubspot) && (() => {
                    const crm = b.crm_history?.salesforce || b.crm_history?.hubspot;
                    const crmName = b.crm_history?.salesforce ? 'Salesforce' : 'HubSpot';
                    return (
                      <SectionCard>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                          <SectionTitle icon={<TrendingUp size={14} />} label={`${crmName} CRM Snapshot`} />
                          {!isCrmConnected && (
                            <span style={{ fontSize: '9px', fontWeight: 800, color: '#f59e0b', backgroundColor: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.25)', padding: '2px 7px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <Shield size={9} />Offline — Historical Snapshot
                            </span>
                          )}
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px 16px' }}>
                          <InfoBlock label="Pipeline Stage" value={crm.pipeline_stage} color="#3E8FA6" />
                          {crm.deal_name && <InfoBlock label="Deal Name" value={crm.deal_name} />}
                          {crm.deal_amount && <InfoBlock label="Deal Amount" value={`$${Number(crm.deal_amount).toLocaleString()}`} color="#10b981" />}
                          {crm.deal_close_date && <InfoBlock label="Expected Close" value={crm.deal_close_date} />}
                          {crm.notes && <div style={{ gridColumn: '1 / -1' }}><InfoBlock label="CRM Notes" value={<span style={{ fontSize: '11px', fontStyle: 'italic', color: 'var(--admin-card-subtext)', lineHeight: 1.5 }}>{crm.notes}</span>} /></div>}
                        </div>
                        {crm.timeline?.length > 0 && (
                          <div style={{ marginTop: '14px', borderTop: '1px solid rgba(255,255,255,0.05)', paddingTop: '14px' }}>
                            <div style={{ fontSize: '10px', color: 'var(--admin-card-subtext)', fontWeight: 700, textTransform: 'uppercase', marginBottom: '10px' }}>Recent Activity</div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', borderLeft: '2px solid rgba(62,143,166,0.2)', paddingLeft: '14px', marginLeft: '6px' }}>
                              {crm.timeline.map((item: any, idx: number) => (
                                <div key={idx} style={{ position: 'relative' }}>
                                  <div style={{ width: '7px', height: '7px', backgroundColor: '#3E8FA6', position: 'absolute', left: '-18px', top: '5px' }} />
                                  <div style={{ fontSize: '11px', fontWeight: 700, color: 'var(--admin-card-text)' }}>{item.type}</div>
                                  {item.note && <p style={{ fontSize: '11px', color: 'var(--admin-card-subtext)', margin: '2px 0 0', lineHeight: 1.4 }}>{item.note}</p>}
                                  {item.date && <div style={{ fontSize: '9px', color: 'var(--admin-card-subtext)', marginTop: '2px' }}>{item.date}</div>}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </SectionCard>
                    );
                  })()}

                  {!isCrmConnected && (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', padding: '10px 14px', background: 'rgba(245,158,11,0.04)', border: '1px solid rgba(245,158,11,0.2)', borderLeft: '3px solid #d97706' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <ShieldAlert size={16} style={{ color: '#d97706', flexShrink: 0 }} />
                        <span style={{ fontSize: '11px', color: '#b45309', lineHeight: 1.4 }}>CRM is disconnected. Pipeline data shown above is a historical snapshot.</span>
                      </div>
                      <Button onClick={() => navigate('/admin/integrations')} size="sm" variant="warning" style={{ border: 'none', flexShrink: 0, width: 'auto', minWidth: '110px' }}>Reconnect CRM</Button>
                    </div>
                  )}
                </div>
              )}

              {/* ═══════ TAB: PROSPECT ═══════ */}
              {activeTab === 'prospect' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {/* Split row: Prospect card + Engagement gauge */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '12px' }}>

                    {/* Prospect Card */}
                    <SectionCard>
                      <SectionTitle icon={<User size={14} />} label="Prospect Profile" />
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 20px' }}>
                        <InfoBlock label="Name" value={ai.prospect_profile?.name} />
                        <InfoBlock label="Role / Title" value={ai.prospect_profile?.role} />
                        {ai.prospect_profile?.tenure && <InfoBlock label="Tenure" value={ai.prospect_profile.tenure} />}
                        {ai.prospect_profile?.location && <InfoBlock label="Location" value={ai.prospect_profile.location} />}
                        {ai.prospect_profile?.seniority && <InfoBlock label="Seniority" value={ai.prospect_profile.seniority} />}
                        {ai.prospect_profile?.linkedin_url && (
                          <div>
                            <div style={{ fontSize: '10px', color: 'var(--admin-card-subtext)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '2px' }}>LinkedIn</div>
                            <a href={ai.prospect_profile.linkedin_url.startsWith('http') ? ai.prospect_profile.linkedin_url : `https://${ai.prospect_profile.linkedin_url}`}
                              target="_blank" rel="noopener noreferrer"
                              style={{ fontSize: '12px', color: '#0077b5', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '4px', textDecoration: 'none' }}>
                              <Linkedin size={11} />View Profile <ArrowUpRight size={10} />
                            </a>
                          </div>
                        )}
                      </div>
                    </SectionCard>

                    {/* Engagement Gauge */}
                    {ai.engagement && (
                      <SectionCard style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px', minWidth: '160px' }}>
                        <SectionTitle icon={<Activity size={14} />} label="Engagement" />
                        <EngagementGauge score={ai.engagement.score} label={ai.engagement.label} />
                        {ai.engagement.tone && (
                          <span style={{ fontSize: '10px', color: 'var(--admin-card-subtext)', textAlign: 'center', fontStyle: 'italic', lineHeight: 1.3, maxWidth: '140px' }}>"{ai.engagement.tone}"</span>
                        )}
                      </SectionCard>
                    )}
                  </div>

                  {/* Interaction Summary */}
                  {ai.prospect_profile?.interaction_summary && (
                    <SectionCard style={{ backgroundColor: 'rgba(62,143,166,0.04)', border: '1px solid rgba(62,143,166,0.15)' }}>
                      <SectionTitle icon={<Sparkles size={14} />} label="AI Interaction Summary" />
                      <p style={{ margin: 0, fontSize: '12px', color: 'var(--admin-card-text)', lineHeight: 1.6 }}>{ai.prospect_profile.interaction_summary}</p>
                    </SectionCard>
                  )}

                  {/* Engagement Summary */}
                  {ai.engagement?.summary && (
                    <SectionCard>
                      <SectionTitle icon={<TrendingUp size={14} />} label="Engagement Summary" />
                      <p style={{ margin: 0, fontSize: '12px', color: 'var(--admin-card-text)', lineHeight: 1.6 }}>{ai.engagement.summary}</p>
                    </SectionCard>
                  )}

                  {/* Subscores */}
                  {ai.engagement?.subscores?.length > 0 && (
                    <SectionCard>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', userSelect: 'none' }} onClick={() => setSubscoresExpanded(e => !e)}>
                        <SectionTitle icon={<BarChart3 size={14} />} label="Score Breakdown" />
                        <button style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--admin-card-subtext)', display: 'flex', alignItems: 'center' }}>
                          {subscoredExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                        </button>
                      </div>
                      {subscoredExpanded && (
                        <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} transition={{ duration: 0.2 }}>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            {ai.engagement.subscores.map((s: any, i: number) => (
                              <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                  <span style={{ fontSize: '11px', fontWeight: 700, color: 'var(--admin-card-text)', textTransform: 'capitalize' }}>{s.name?.replace(/_/g, ' ')}</span>
                                  <span style={{ fontSize: '11px', fontWeight: 800, color: '#3E8FA6' }}>{Math.round(s.score * 100)}</span>
                                </div>
                                <div style={{ height: '4px', backgroundColor: 'rgba(255,255,255,0.06)', borderRadius: '2px', overflow: 'hidden' }}>
                                  <motion.div initial={{ width: 0 }} animate={{ width: `${s.score * 100}%` }} transition={{ duration: 0.8, ease: 'easeOut' }}
                                    style={{ height: '100%', backgroundColor: '#3E8FA6', borderRadius: '2px' }} />
                                </div>
                                <span style={{ fontSize: '10px', color: 'var(--admin-card-subtext)', fontStyle: 'italic' }}>{s.basis}</span>
                              </div>
                            ))}
                          </div>
                        </motion.div>
                      )}
                    </SectionCard>
                  )}
                </div>
              )}

              {/* ═══════ TAB: COMPANY ═══════ */}
              {activeTab === 'company' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {ai.company_profile ? (
                    <>
                      {/* Company Header Card */}
                      <SectionCard>
                        <SectionTitle icon={<Building2 size={14} />} label="Company Overview" />
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px 20px' }}>
                          <InfoBlock label="Company" value={ai.company_profile.name} />
                          {ai.company_profile.industry && <InfoBlock label="Industry" value={ai.company_profile.industry} />}
                          {ai.company_profile.hq_location && <InfoBlock label="HQ Location" value={ai.company_profile.hq_location} />}
                          {ai.company_profile.size_band && <InfoBlock label="Employees" value={ai.company_profile.size_band} />}
                          {ai.company_profile.annual_revenue && <InfoBlock label="Annual Revenue" value={ai.company_profile.annual_revenue} color="#10b981" />}
                          {ai.company_profile.domain && (
                            <div>
                              <div style={{ fontSize: '10px', color: 'var(--admin-card-subtext)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '2px' }}>Website</div>
                              <a href={`https://${ai.company_profile.domain}`} target="_blank" rel="noopener noreferrer"
                                style={{ fontSize: '12px', color: '#3E8FA6', fontWeight: 700, textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <Globe size={11} />{ai.company_profile.domain}
                              </a>
                            </div>
                          )}
                        </div>
                        {ai.company_profile.description && (
                          <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid rgba(255,255,255,0.05)' }}>
                            <div style={{ fontSize: '10px', color: 'var(--admin-card-subtext)', fontWeight: 700, textTransform: 'uppercase', marginBottom: '6px' }}>About</div>
                            <p style={{ margin: 0, fontSize: '11px', color: 'var(--admin-card-subtext)', lineHeight: 1.6 }}>{ai.company_profile.description}</p>
                          </div>
                        )}
                      </SectionCard>

                      {/* Tech Stack */}
                      {ai.company_profile.tech_stack?.length > 0 && (
                        <SectionCard>
                          <SectionTitle icon={<Cpu size={14} />} label="Tech Stack"
                            badge={<span style={{ fontSize: '10px', fontWeight: 700, color: 'var(--admin-card-subtext)', padding: '1px 6px', border: '1px solid var(--admin-card-border)', backgroundColor: 'rgba(255,255,255,0.05)' }}>{ai.company_profile.tech_stack.length} tools</span>} />
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
                            {(techExpanded ? ai.company_profile.tech_stack : ai.company_profile.tech_stack.slice(0, 20)).map((t: string, i: number) => (
                              <TechChip key={i} label={t} />
                            ))}
                          </div>
                          {ai.company_profile.tech_stack.length > 20 && (
                            <button onClick={() => setTechExpanded(e => !e)}
                              style={{ marginTop: '8px', background: 'none', border: 'none', cursor: 'pointer', fontSize: '10px', fontWeight: 700, color: '#3E8FA6', display: 'flex', alignItems: 'center', gap: '4px', padding: 0, fontFamily: 'inherit' }}>
                              {techExpanded ? <><ChevronUp size={11} />Show less</> : <><ChevronDown size={11} />Show {ai.company_profile.tech_stack.length - 20} more</>}
                            </button>
                          )}
                        </SectionCard>
                      )}

                      {/* Recent News */}
                      {ai.company_profile.recent_news?.length > 0 && (
                        <SectionCard>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                            <SectionTitle icon={<Newspaper size={14} />} label="Recent News" />
                            <button onClick={() => setNewsExpanded(e => !e)}
                              style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--admin-card-subtext)', display: 'flex', alignItems: 'center', gap: '4px', fontSize: '10px', fontWeight: 600, fontFamily: 'inherit' }}>
                              {newsExpanded ? <><ChevronUp size={11} />Collapse</> : <><ChevronDown size={11} />Expand all</>}
                            </button>
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            {ai.company_profile.recent_news.map((news: any, i: number) => (
                              <div key={i} style={{ padding: '10px 12px', backgroundColor: 'rgba(255,255,255,0.01)', border: '1px solid rgba(255,255,255,0.04)', borderLeft: '3px solid rgba(56,189,248,0.3)' }}>
                                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '8px' }}>
                                  <span style={{ fontSize: '12px', fontWeight: 700, color: 'var(--admin-card-text)', lineHeight: 1.4 }}>{news.headline}</span>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
                                    {news.category && <span style={{ fontSize: '9px', fontWeight: 700, color: '#38bdf8', backgroundColor: 'rgba(56,189,248,0.08)', border: '1px solid rgba(56,189,248,0.2)', padding: '1px 5px', textTransform: 'uppercase' }}>{news.category}</span>}
                                    {news.source_url && <a href={news.source_url} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--admin-card-subtext)', display: 'flex' }}><ExternalLink size={11} /></a>}
                                  </div>
                                </div>
                                {news.date && <div style={{ fontSize: '9px', color: 'var(--admin-card-subtext)', marginTop: '3px' }}>{news.date}</div>}
                                {(newsExpanded || i < 2) && news.summary && (
                                  <p style={{ margin: '6px 0 0', fontSize: '11px', color: 'var(--admin-card-subtext)', lineHeight: 1.5 }}>{news.summary}</p>
                                )}
                              </div>
                            ))}
                          </div>
                        </SectionCard>
                      )}
                    </>
                  ) : (
                    <div style={{ padding: '32px', textAlign: 'center', color: 'var(--admin-card-subtext)', fontSize: '12px' }}>
                      No company intelligence available for this meeting.
                    </div>
                  )}
                </div>
              )}

              {/* ═══════ TAB: TACTICAL PLAYBOOK ═══════ */}
              {activeTab === 'tactical' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {ai.tactical_recommendations ? (
                    <>
                      {/* Conversation Hooks */}
                      {ai.tactical_recommendations.hooks?.length > 0 && (
                        <SectionCard style={{ backgroundColor: 'rgba(16,185,129,0.03)', border: '1px solid rgba(16,185,129,0.12)' }}>
                          <SectionTitle icon={<Zap size={14} />} label="Conversation Hooks" />
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '7px' }}>
                            {ai.tactical_recommendations.hooks.map((hook: string, i: number) => (
                              <div key={i} style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '8px', padding: '8px 12px', backgroundColor: 'rgba(16,185,129,0.04)', border: '1px solid rgba(16,185,129,0.1)' }}>
                                <span style={{ fontSize: '12px', color: 'var(--admin-card-text)', lineHeight: 1.5, flex: 1 }}>• {hook}</span>
                                <CopyButton text={hook} />
                              </div>
                            ))}
                          </div>
                        </SectionCard>
                      )}

                      {/* Warning panel: Topics to Avoid + Key Risks */}
                      {((ai.tactical_recommendations.topics_to_avoid?.length > 0) || (ai.tactical_recommendations.key_risks?.length > 0)) && (
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                          {ai.tactical_recommendations.topics_to_avoid?.length > 0 && (
                            <SectionCard style={{ backgroundColor: 'rgba(239,68,68,0.03)', border: '1px solid rgba(239,68,68,0.15)' }}>
                              <SectionTitle icon={<XCircle size={14} />} label="Topics to Avoid" />
                              <ul style={{ margin: 0, paddingLeft: '16px', display: 'flex', flexDirection: 'column', gap: '5px' }}>
                                {ai.tactical_recommendations.topics_to_avoid.map((t: string, i: number) => (
                                  <li key={i} style={{ fontSize: '11px', color: '#f87171', lineHeight: 1.5 }}>{t}</li>
                                ))}
                              </ul>
                            </SectionCard>
                          )}
                          {ai.tactical_recommendations.key_risks?.length > 0 && (
                            <SectionCard style={{ backgroundColor: 'rgba(245,158,11,0.03)', border: '1px solid rgba(245,158,11,0.15)' }}>
                              <SectionTitle icon={<AlertTriangle size={14} />} label="Key Risks" />
                              <ul style={{ margin: 0, paddingLeft: '16px', display: 'flex', flexDirection: 'column', gap: '5px' }}>
                                {ai.tactical_recommendations.key_risks.map((r: string, i: number) => (
                                  <li key={i} style={{ fontSize: '11px', color: '#fbbf24', lineHeight: 1.5 }}>{r}</li>
                                ))}
                              </ul>
                            </SectionCard>
                          )}
                        </div>
                      )}

                      {/* Communication Style */}
                      {ai.tactical_recommendations.communication_style && (
                        <SectionCard>
                          <SectionTitle icon={<User size={14} />} label="Communication Style" />
                          <p style={{ margin: 0, fontSize: '12px', color: 'var(--admin-card-text)', lineHeight: 1.6, textTransform: 'capitalize' }}>
                            {ai.tactical_recommendations.communication_style}
                          </p>
                        </SectionCard>
                      )}

                      {/* Recommended Next Step — Prominent CTA box */}
                      {ai.tactical_recommendations.recommended_next_step && (
                        <div style={{ padding: '14px 16px', backgroundColor: 'rgba(62,143,166,0.06)', border: '1px solid rgba(62,143,166,0.25)', borderLeft: '4px solid #3E8FA6' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '7px', marginBottom: '8px' }}>
                            <Target size={15} style={{ color: '#3E8FA6' }} />
                            <span style={{ fontSize: '11px', fontWeight: 800, color: '#3E8FA6', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Recommended Next Step</span>
                          </div>
                          <p style={{ margin: 0, fontSize: '13px', fontWeight: 600, color: 'var(--admin-card-text)', lineHeight: 1.6 }}>
                            {ai.tactical_recommendations.recommended_next_step}
                          </p>
                        </div>
                      )}
                    </>
                  ) : (
                    <div style={{ padding: '32px', textAlign: 'center', color: 'var(--admin-card-subtext)', fontSize: '12px' }}>
                      No tactical recommendations generated.
                    </div>
                  )}
                </div>
              )}

              {/* ═══════ TAB: BATTLECARDS ═══════ */}
              {activeTab === 'battlecards' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {/* Check both old and new AI brief schemas */}
                  {(() => {
                    const cards = ai.competitive_intelligence?.battlecards || briefingDetail.competitive_intel?.dynamic_battlecards;
                    const detected = ai.competitive_intelligence?.detected || briefingDetail.competitive_intel?.detected_competitors?.map((c: any) => c.name || c);

                    if (!detected?.length && !cards?.length) {
                      return (
                        <div style={{ padding: '32px 24px', border: '1px dashed var(--admin-card-border)', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
                          <Brain size={28} style={{ color: 'var(--admin-card-subtext)', opacity: 0.5 }} />
                          <span style={{ fontSize: '13px', fontWeight: 800, color: 'var(--admin-card-text)' }}>No Competitors Detected</span>
                          <p style={{ fontSize: '11px', color: 'var(--admin-card-subtext)', margin: 0, lineHeight: 1.5, maxWidth: '320px' }}>
                            Competitor battlecards will appear here when competitor mentions are identified in CRM notes, emails, or meeting context.
                          </p>
                        </div>
                      );
                    }

                    const battlecards = ai.competitive_intelligence?.battlecards || [];

                    return (
                      <>
                        {/* Competitor tabs */}
                        {battlecards.length > 0 && (
                          <>
                            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                              {battlecards.map((card: any, i: number) => (
                                <button key={i} onClick={() => setActiveCompetitor(i)}
                                  style={{ padding: '5px 12px', border: `1px solid ${activeCompetitor === i ? '#ef4444' : 'var(--admin-card-border)'}`, background: activeCompetitor === i ? 'rgba(239,68,68,0.08)' : 'transparent', color: activeCompetitor === i ? '#ef4444' : 'var(--admin-card-subtext)', fontSize: '11px', fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '5px', fontFamily: 'inherit', transition: 'all 0.2s' }}>
                                  <Swords size={11} />{card.competitor}
                                </button>
                              ))}
                            </div>

                            {battlecards[activeCompetitor] && (() => {
                              const card = battlecards[activeCompetitor];
                              return (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                  {/* Strengths vs Weaknesses */}
                                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                                    <SectionCard style={{ backgroundColor: 'rgba(16,185,129,0.03)', border: '1px solid rgba(16,185,129,0.15)' }}>
                                      <SectionTitle icon={<CheckCircle size={13} />} label="Our Strengths vs Them" />
                                      {card.our_strengths?.length > 0 ? (
                                        <ul style={{ margin: 0, paddingLeft: '16px', display: 'flex', flexDirection: 'column', gap: '5px' }}>
                                          {card.our_strengths.map((s: string, i: number) => <li key={i} style={{ fontSize: '11px', color: '#10b981', lineHeight: 1.5 }}>{s}</li>)}
                                        </ul>
                                      ) : (
                                        <p style={{ margin: 0, fontSize: '11px', color: 'var(--admin-card-subtext)', fontStyle: 'italic' }}>No specific strengths profiled yet.</p>
                                      )}
                                    </SectionCard>
                                    <SectionCard style={{ backgroundColor: 'rgba(239,68,68,0.03)', border: '1px solid rgba(239,68,68,0.15)' }}>
                                      <SectionTitle icon={<XCircle size={13} />} label="Our Weaknesses" />
                                      {card.our_weaknesses?.length > 0 ? (
                                        <ul style={{ margin: 0, paddingLeft: '16px', display: 'flex', flexDirection: 'column', gap: '5px' }}>
                                          {card.our_weaknesses.map((w: string, i: number) => <li key={i} style={{ fontSize: '11px', color: '#f87171', lineHeight: 1.5 }}>{w}</li>)}
                                        </ul>
                                      ) : (
                                        <p style={{ margin: 0, fontSize: '11px', color: 'var(--admin-card-subtext)', fontStyle: 'italic' }}>No weaknesses profiled for this competitor.</p>
                                      )}
                                    </SectionCard>
                                  </div>

                                  {/* Positioning */}
                                  {card.positioning && (
                                    <SectionCard style={{ backgroundColor: 'rgba(62,143,166,0.04)', border: '1px solid rgba(62,143,166,0.15)' }}>
                                      <SectionTitle icon={<Target size={13} />} label="Positioning Strategy" />
                                      <p style={{ margin: 0, fontSize: '12px', color: 'var(--admin-card-text)', lineHeight: 1.6 }}>{card.positioning}</p>
                                    </SectionCard>
                                  )}

                                  {/* Recent Moves */}
                                  {card.recent_moves?.length > 0 && (
                                    <SectionCard>
                                      <SectionTitle icon={<TrendingUp size={13} />} label="Recent Moves" />
                                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                        {card.recent_moves.map((move: any, i: number) => (
                                          <div key={i} style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '10px', padding: '8px 12px', border: '1px solid rgba(245,158,11,0.15)', backgroundColor: 'rgba(245,158,11,0.03)' }}>
                                            <div style={{ flex: 1 }}>
                                              {move.category && <span style={{ fontSize: '9px', fontWeight: 800, color: '#f59e0b', textTransform: 'uppercase', display: 'block', marginBottom: '3px' }}>{move.category}</span>}
                                              <span style={{ fontSize: '12px', fontWeight: 700, color: 'var(--admin-card-text)', lineHeight: 1.4, display: 'block' }}>{move.headline}</span>
                                              {move.date && <span style={{ fontSize: '9px', color: 'var(--admin-card-subtext)', marginTop: '3px', display: 'block' }}>{move.date}</span>}
                                            </div>
                                            {move.source_url && (
                                              <a href={move.source_url} target="_blank" rel="noopener noreferrer"
                                                style={{ color: '#3E8FA6', flexShrink: 0, display: 'flex', alignItems: 'center', gap: '3px', fontSize: '10px', fontWeight: 600, textDecoration: 'none' }}>
                                                <ExternalLink size={11} />Source
                                              </a>
                                            )}
                                          </div>
                                        ))}
                                      </div>
                                    </SectionCard>
                                  )}
                                </div>
                              );
                            })()}
                          </>
                        )}

                        {/* Old-schema fallback: detected_competitors + dynamic_battlecards */}
                        {!battlecards.length && briefingDetail.competitive_intel?.dynamic_battlecards?.length > 0 && (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                            {briefingDetail.competitive_intel.dynamic_battlecards.map((card: any, idx: number) => (
                              <div key={idx} style={{ padding: '12px', borderLeft: '4px solid #38bdf8', backgroundColor: 'rgba(56,189,248,0.03)', border: '1px solid rgba(56,189,248,0.1)' }}>
                                <div style={{ fontSize: '10px', color: '#38bdf8', fontWeight: 800, textTransform: 'uppercase', marginBottom: '4px' }}>Objection vs {card.competitor}</div>
                                <p style={{ fontSize: '12px', fontWeight: 700, color: 'var(--admin-card-text)', margin: '0 0 8px', fontStyle: 'italic' }}>"{card.objection}"</p>
                                <div style={{ fontSize: '10px', color: 'var(--admin-card-subtext)', fontWeight: 700, textTransform: 'uppercase', marginBottom: '2px' }}>Rebuttal</div>
                                <p style={{ fontSize: '11px', color: 'var(--admin-card-text)', margin: 0, lineHeight: 1.4 }}>{card.rebuttal}</p>
                              </div>
                            ))}
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>
              )}

            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
